import { c as _c } from "react/compiler-runtime";
import type { Locale } from '@onefootprint/request-types';
import { cx } from 'class-variance-authority';
import { formatNumeral, NumeralThousandGroupStyles } from 'cleave-zen';
import { useId } from 'react';
import { type FieldValues, type Path, type PathValue, type RegisterOptions, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextInput from '../../text-input';
import FormWrapper from '../internals/form-wrapper';
import type { FormInputProps } from './form-input';
export type FormNumberProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  onChange?: (value: number | null) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  locale?: Locale;
  defaultValue?: number;
  decimalPlaces?: number;
  allowNegative?: boolean;
  thousandsSeparator?: boolean;
  placeholder?: string; // Placeholder prop for number input
} & Omit<FormInputProps<TFieldValues>, 'name' | 'rules' | 'type' | 'autoComplete' | 'onChange' | 'defaultValue' | 'mask'>;
export default function FormNumber(t0) {
  const $ = _c(48);
  const {
    rules,
    name,
    label,
    hint,
    addon,
    errorPosition: t1,
    orientation: t2,
    size: t3,
    id: baseId,
    onChange,
    locale: t4,
    defaultValue: defaultValueProp,
    decimalPlaces: t5,
    allowNegative: t6,
    thousandsSeparator: t7,
    placeholder,
    ...props
  } = t0;
  const errorPosition = t1 === undefined ? "relative" : t1;
  const orientation = t2 === undefined ? "vertical" : t2;
  const size = t3 === undefined ? "default" : t3;
  const locale = t4 === undefined ? "en-US" : t4;
  const decimalPlaces = t5 === undefined ? 2 : t5;
  const allowNegative = t6 === undefined ? true : t6;
  const thousandsSeparator = t7 === undefined ? true : t7;
  const {
    t
  } = useTranslation("ui");
  const {
    register,
    setValue,
    control,
    getFieldState
  } = useFormContext();
  const formState = useFormState({
    control,
    name,
    disabled: props.disabled
  });
  let t8;
  if ($[0] !== formState || $[1] !== getFieldState || $[2] !== name) {
    t8 = getFieldState(name, formState);
    $[0] = formState;
    $[1] = getFieldState;
    $[2] = name;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const {
    error
  } = t8;
  const contextId = useId();
  const id = baseId || contextId;
  let t9;
  if ($[4] !== allowNegative || $[5] !== decimalPlaces || $[6] !== locale || $[7] !== thousandsSeparator) {
    t9 = value => {
      if (value === null || value === undefined || value === "") {
        return "";
      }
      return formatNumeral(value.toString(), {
        delimiter: locale === "en-US" ? "," : ".",
        numeralDecimalMark: locale === "en-US" ? "." : ",",
        numeralThousandsGroupStyle: thousandsSeparator ? NumeralThousandGroupStyles.THOUSAND : NumeralThousandGroupStyles.NONE,
        numeralDecimalScale: decimalPlaces,
        numeralIntegerScale: 10,
        numeralPositiveOnly: !allowNegative
      });
    };
    $[4] = allowNegative;
    $[5] = decimalPlaces;
    $[6] = locale;
    $[7] = thousandsSeparator;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const getFormattedValue = t9;
  let t10;
  bb0: {
    if (!defaultValueProp) {
      t10 = undefined;
      break bb0;
    }
    let t11;
    if ($[9] !== defaultValueProp || $[10] !== locale) {
      t11 = locale === "en-US" ? defaultValueProp : defaultValueProp.toString().replace(".", ",");
      $[9] = defaultValueProp;
      $[10] = locale;
      $[11] = t11;
    } else {
      t11 = $[11];
    }
    const value_0 = t11;
    let t12;
    if ($[12] !== getFormattedValue || $[13] !== value_0) {
      t12 = getFormattedValue(value_0);
      $[12] = getFormattedValue;
      $[13] = value_0;
      $[14] = t12;
    } else {
      t12 = $[14];
    }
    t10 = t12;
  }
  const defaultValue = t10;
  let t11;
  if ($[15] !== locale) {
    t11 = value_1 => {
      if (typeof value_1 === "number") {
        return value_1;
      }
      if (typeof value_1 === "string") {
        let cleaned = value_1.replace(/[^\d.,-]/g, "");
        if (locale === "en-US") {
          cleaned = cleaned.replace(/,/g, "");
        } else {
          cleaned = cleaned.replace(/\./g, "").replace(",", ".");
        }
        const num = parseFloat(cleaned);
        return Number.isNaN(num) ? null : num;
      }
      return null;
    };
    $[15] = locale;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== allowNegative || $[18] !== decimalPlaces || $[19] !== t) {
    t12 = value_2 => {
      if (value_2 === null || value_2 === undefined) {
        return true;
      }
      if (!allowNegative && value_2 < 0) {
        return t("components.form.number.errors.negative-not-allowed");
      }
      if (decimalPlaces === 0 && !Number.isInteger(value_2)) {
        return t("components.form.number.errors.whole-numbers-only");
      }
      return true;
    };
    $[17] = allowNegative;
    $[18] = decimalPlaces;
    $[19] = t;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const numberRules = {
    setValueAs: t11,
    validate: t12,
    ...rules
  };
  const T0 = FormWrapper;
  const t13 = error?.message;
  const t14 = true;
  const t15 = formState.disabled || props.disabled;
  const T1 = TextInput;
  let t16;
  if ($[21] !== getFormattedValue || $[22] !== name || $[23] !== onChange || $[24] !== setValue) {
    t16 = t17 => {
      const {
        target: t18
      } = t17;
      const {
        value: value_3
      } = t18;
      const formattedValue = getFormattedValue(value_3);
      setValue(name, formattedValue as PathValue<TFieldValues, Path<TFieldValues>>);
      onChange?.(parseFloat(formattedValue.replace(/[^\d.,-]/g, "")) || null);
    };
    $[21] = getFormattedValue;
    $[22] = name;
    $[23] = onChange;
    $[24] = setValue;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const t17 = register(name, {
    ...numberRules,
    disabled: formState.disabled || props.disabled,
    onChange: t16
  });
  const t18 = !!error;
  const t19 = "text";
  const t20 = "decimal";
  const t21 = "mask";
  const t22 = cx({
    "rounded-bl-none! rounded-tl-none!": addon
  }, props.className);
  const t23 = placeholder || (decimalPlaces > 0 ? "0.00" : "0");
  let t24;
  if ($[26] !== T1 || $[27] !== defaultValue || $[28] !== id || $[29] !== props || $[30] !== size || $[31] !== t17 || $[32] !== t18 || $[33] !== t22 || $[34] !== t23) {
    t24 = <T1 {...t17} hasError={t18} id={id} defaultValue={defaultValue} size={size} type={t19} inputMode={t20} data-dd-privacy={t21} className={t22} placeholder={t23} {...props} />;
    $[26] = T1;
    $[27] = defaultValue;
    $[28] = id;
    $[29] = props;
    $[30] = size;
    $[31] = t17;
    $[32] = t18;
    $[33] = t22;
    $[34] = t23;
    $[35] = t24;
  } else {
    t24 = $[35];
  }
  let t25;
  if ($[36] !== T0 || $[37] !== addon || $[38] !== errorPosition || $[39] !== hint || $[40] !== id || $[41] !== label || $[42] !== orientation || $[43] !== size || $[44] !== t13 || $[45] !== t15 || $[46] !== t24) {
    t25 = <T0 id={id} error={t13} label={label} errorPosition={errorPosition} size={size} hint={hint} addon={addon} orientation={orientation} fullWidth={t14} disabled={t15}>{t24}</T0>;
    $[36] = T0;
    $[37] = addon;
    $[38] = errorPosition;
    $[39] = hint;
    $[40] = id;
    $[41] = label;
    $[42] = orientation;
    $[43] = size;
    $[44] = t13;
    $[45] = t15;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  return t25;
}